import './index.scss'
import 'react-datepicker/dist/react-datepicker.css'

import { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'

import { useAuth } from '@auth/AuthProvider'
import ErrorView from '@components/ErrorView'
import LoadingIndicator from '@components/LoadingIndicator'
import Modal from '@components/Modal'
import TrainingModuleCard from '@components/TrainingModuleCard'
import TrainingController from '@data/controller/TrainingController'
import useTrainingModules from '@hooks/useTrainingModules'
import {
  TrainingModule,
  TrainingVolunteerAssignmentBundle,
  trainingAssignmentFromResponse
} from '@interfaces/training.interface'

import backIcon from 'url:@icon/chevron.left.plain.svg';
import forwardIcon from 'url:@icon/arrow.right.plain.svg';
import bookIcon from 'url:@assets/icons/book.svg';
import checkmarkIcon from 'url:@icon/checkmark.svg';
import closeIcon from 'url:@icon/close.svg';

// Picking the Module comes first, then everything about the assignment itself.
type AssignStage = 'module' | 'details';

// The stage being left, kept mounted long enough to slide out from under the
// one arriving.
type StageTransition = {
  from: AssignStage;
  direction: 'forward' | 'back';
};

const DEFAULT_DUE_DAYS = 14;

// Matches the slide in index.scss.
const STAGE_TRANSITION_MS = 240;

function defaultDueDate() {
  const date = new Date();
  date.setDate(date.getDate() + DEFAULT_DUE_DAYS);

  return date;
}

// The picker only chooses a day, so the time rides along from whenever the
// modal was opened. Due at the end of that day is what "due Sep 20" means.
function endOfDay(date: Date) {
  const endOfDate = new Date(date);
  endOfDate.setHours(23, 59, 59, 999);

  return endOfDate;
}

export default function AssignTrainingModal(props: {
  isOpen: boolean,
  teamID?: string,
  personID?: string,
  personName: string,
  /// Modules this volunteer already has, so the same one can't be assigned twice.
  assignedModuleIDs: string[],
  close: () => void,
  /// Carries the created assignment when the server hands one back, so the page
  /// behind can show it before its own refetch lands.
  onAssigned: (bundle: TrainingVolunteerAssignmentBundle | null) => void
}) {
  const auth = useAuth();
  const { modules, loading, hasLoaded, error: loadError, refetch } = useTrainingModules();

  const [stage, setStage] = useState<AssignStage>('module');
  const [transition, setTransition] = useState<StageTransition | null>(null);
  const [selectedModule, setSelectedModule] = useState<TrainingModule | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(defaultDueDate);
  const [notifyOnCompletion, setNotifyOnCompletion] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!transition) return;

    const timeout = window.setTimeout(() => setTransition(null), STAGE_TRANSITION_MS);

    return () => window.clearTimeout(timeout);
  }, [transition]);

  const changeStage = (nextStage: AssignStage) => {
    if (nextStage === stage) return;

    setTransition({ from: stage, direction: nextStage === 'details' ? 'forward' : 'back' });
    setStage(nextStage);
    setError(null);
  };

  const sortedModules = [...modules].sort((one, two) => {
    return one.attributes.name.localeCompare(two.attributes.name);
  });

  const assignedModuleIDs = new Set(props.assignedModuleIDs);
  const canSave = Boolean(selectedModule) && dueDate !== null && !isSaving;

  const assign = async () => {
    if (!auth.churchId || !props.teamID || !props.personID || !selectedModule || !dueDate) {
      setError('Missing the context needed to assign this Module. Please refresh and try again.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await TrainingController.assignTrainingModule(
        auth.churchId,
        props.teamID,
        props.personID,
        {
          moduleID: selectedModule.id,
          dueDate: endOfDay(dueDate).toISOString(),
          notifyStaffAssignerOnCompletion: notifyOnCompletion
        },
        auth
      );

      if (response.error) {
        setError(response.error);
        return;
      }

      const created = trainingAssignmentFromResponse(response.body);

      props.onAssigned(created ? { assignment: created, module: selectedModule } : null);
    } finally {
      setIsSaving(false);
    }
  };

  const renderStage = (target: AssignStage) => {
    if (target === 'module') {
      return <ModuleStep
        modules={sortedModules}
        loading={loading && !hasLoaded}
        error={loadError}
        assignedModuleIDs={assignedModuleIDs}
        selectedModuleID={selectedModule?.id ?? null}
        onRetry={() => refetch()}
        onSelect={setSelectedModule}
      />
    }

    return <DetailsStep
      module={selectedModule}
      personName={props.personName}
      isReassignment={selectedModule !== null && assignedModuleIDs.has(selectedModule.id)}
      dueDate={dueDate}
      notifyOnCompletion={notifyOnCompletion}
      isSaving={isSaving}
      onDueDateChange={setDueDate}
      onNotifyChange={setNotifyOnCompletion}
    />
  };

  const isPickingModule = stage === 'module';

  return <Modal
    id='assign-training-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='assign-training-modal-content'>
      <div className='header'>
        <button
          type='button'
          className={isPickingModule ? 'header-control close' : 'header-control back'}
          disabled={isSaving}
          onClick={() => {
            if (isPickingModule) {
              props.close();
              return;
            }

            changeStage('module');
          }}>
          {isPickingModule ?
            <img src={closeIcon} alt='Close icon' /> :
            <img src={backIcon} alt='Back icon' />}
        </button>

        <h3 className='title'>{isPickingModule ? 'Select a Module' : 'Assignment Details'}</h3>

        {isPickingModule ? <button
          type='button'
          className='header-control forward'
          disabled={!selectedModule}
          onClick={() => changeStage('details')}>
          <img src={forwardIcon} alt='Continue icon' />
        </button> : <button
          type='button'
          className={isSaving ? 'header-control confirm saving' : 'header-control confirm'}
          disabled={!canSave}
          onClick={() => {
            void assign();
          }}>
          {isSaving ?
            <span className='header-control-spinner' role='status' aria-label='Assigning' /> :
            <img src={checkmarkIcon} alt='Assign icon' />}
        </button>}
      </div>

      <div className='body'>
        {error ? <p className='save-error'>{error}</p> : null}

        <div className='stage-viewport'>
          {transition ? <div
            className={`stage is-outgoing ${transition.direction}`}
            aria-hidden='true'>
            {renderStage(transition.from)}
          </div> : null}

          <div className={transition ? `stage is-incoming ${transition.direction}` : 'stage'}>
            {renderStage(stage)}
          </div>
        </div>
      </div>
    </div>
  </Modal>
}

function ModuleStep(props: {
  modules: TrainingModule[];
  loading: boolean;
  error: string | null;
  assignedModuleIDs: Set<string>;
  selectedModuleID: string | null;
  onRetry: () => void;
  onSelect: (module: TrainingModule) => void;
}) {
  if (props.loading) {
    return <div className='assign-training-state'>
      <LoadingIndicator fullScreen={false} />
    </div>
  }

  if (props.error) {
    return <ErrorView
      error={props.error}
      buttonText='Retry'
      buttonAction={props.onRetry} />
  }

  if (props.modules.length === 0) {
    return <ErrorView
      icon={bookIcon}
      title='No Modules Yet'
      error='Create a Training Module before assigning one.'
      showButton={false} />
  }

  return <div className='module-picker'>
    {props.modules.map((module) => {
      const isAssigned = props.assignedModuleIDs.has(module.id);

      return <TrainingModuleCard
        key={module.id}
        module={module}
        isSelected={module.id === props.selectedModuleID}
        badge={isAssigned ? 'Already assigned' : undefined}
        onClick={() => props.onSelect(module)}
      />
    })}
  </div>
}

function DetailsStep(props: {
  module: TrainingModule | null;
  personName: string;
  /// The volunteer already has this Module, so assigning starts them over.
  isReassignment: boolean;
  dueDate: Date | null;
  notifyOnCompletion: boolean;
  isSaving: boolean;
  onDueDateChange: (date: Date | null) => void;
  onNotifyChange: (notify: boolean) => void;
}) {
  return <>
    {props.module ? <div className='selected-module'>
      <TrainingModuleCard module={props.module} />
    </div> : null}

    {props.isReassignment ? <p className='reassign-warning'>
      Assigning this Module again will remove {props.personName}'s existing progress.
    </p> : null}

    <div className='field-container'>
      <label htmlFor='assignment-due-date'>Due Date</label>

      <DatePicker
        id='assignment-due-date'
        className='date-picker'
        selected={props.dueDate}
        disabled={props.isSaving}
        minDate={new Date()}
        dateFormat='PP'
        // The calendar would otherwise be clipped by the modal's own scrolling.
        popperProps={{ strategy: 'fixed' }}
        onChange={(date) => {
          if (props.isSaving) return;

          props.onDueDateChange(date);
        }} />
    </div>

    <label className='checkbox-field'>
      <input
        type='checkbox'
        checked={props.notifyOnCompletion}
        disabled={props.isSaving}
        onChange={(e) => props.onNotifyChange(e.target.checked)}
      />
      <span>Notify me when {props.personName} finishes this Module</span>
    </label>
  </>
}
